import React, { useEffect } from 'react';
import { CheckCircle, Package, FileText, Truck, MapPin } from 'lucide-react';

const CourierToUSAPage = () => {
  useEffect(() => {
    document.title = 'Send Courier from India to USA | Fast & Affordable International Shipping | XFas';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Ship parcels, documents & commercial cargo from India to USA with XFas Logistics. Multiple carrier options, real-time tracking, customs support & door-to-door delivery. Get a quote today.');
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gray-900 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-5xl mb-4">🇺🇸</div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Send Courier from India to USA</h1>
          <p className="text-xl text-orange-400 font-medium">Fast, Reliable, Multichannel</p>
          <p className="mt-4 text-gray-300 text-lg">Door-to-Door Delivery from Any City in India to All 50 US States</p>
          <a href="/#get-quote" className="inline-block mt-8 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors">
            Get a Quote
          </a>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-16 space-y-16">

        {/* Why XFas */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Ship India to USA with XFas?</h2>
          <ul className="space-y-3">
            {[
              ['🚀', 'Express delivery in 2–5 business days via premium air express services'],
              ['💰', 'Economy options available for non-urgent parcels and larger consignments'],
              ['📦', 'All shipment types accepted — documents, gifts, garments, electronics, commercial samples, e-commerce orders'],
              ['🛃', 'Full customs clearance assistance — HS codes, commercial invoices, and US CBP compliance handled for you'],
              ['📍', 'Door-to-door delivery across all US states including Alaska and Hawaii'],
              ['📲', 'Real-time tracking from pickup in India to delivery in the USA'],
              ['🏢', 'Dedicated support for e-commerce sellers shipping Indian products to US customers'],
            ].map(([icon, text]) => (
              <li key={text} className="flex gap-3 text-gray-600">
                <span className="flex-shrink-0">{icon}</span>
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* How It Works */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-8">How It Works</h2>
          <div className="grid md:grid-cols-5 gap-4">
            {[
              { icon: <FileText className="w-6 h-6" />, step: '1', title: 'Get a Quote', desc: 'Enter shipment details and compare carrier rates instantly' },
              { icon: <MapPin className="w-6 h-6" />, step: '2', title: 'Schedule Pickup', desc: 'We collect from your home or office anywhere in India' },
              { icon: <CheckCircle className="w-6 h-6" />, step: '3', title: 'Documentation', desc: 'Our team assists with customs paperwork and labelling' },
              { icon: <Truck className="w-6 h-6" />, step: '4', title: 'Dispatched', desc: 'Routed via the most efficient carrier path' },
              { icon: <Package className="w-6 h-6" />, step: '5', title: 'Delivered', desc: 'Real-time updates until final delivery in the USA' },
            ].map(({ icon, step, title, desc }) => (
              <div key={step} className="text-center">
                <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 text-lg font-bold">{step}</div>
                <div className="text-orange-500 flex justify-center mb-2">{icon}</div>
                <p className="font-semibold text-gray-800 text-sm">{title}</p>
                <p className="text-gray-500 text-xs mt-1">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Shipping Options */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Shipping Options: India to USA</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="text-left p-4 rounded-tl-lg">Service</th>
                  <th className="text-left p-4">Transit Time</th>
                  <th className="text-left p-4 rounded-tr-lg">Best For</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Air Express', '2–5 Business Days', 'Urgent documents, time-sensitive parcels'],
                  ['Economy Air', '7–12 Business Days', 'Non-urgent packages, personal shipments'],
                  ['Commercial Cargo', 'Customised', 'B2B bulk consignments'],
                ].map(([service, time, best], i) => (
                  <tr key={service} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="p-4 font-semibold text-gray-800">{service}</td>
                    <td className="p-4 text-gray-600">{time}</td>
                    <td className="p-4 text-gray-600">{best}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* What Can You Ship */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What Can You Ship?</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              'Business documents and legal papers',
              'Personal gifts and parcels',
              'Clothing, textiles, and handicrafts',
              'Electronics and gadgets (with applicable compliance)',
              'E-commerce orders and commercial samples',
              'Pharmaceuticals (subject to US FDA regulations)',
            ].map((item) => (
              <div key={item} className="flex gap-3 items-start">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Customs */}
        <section className="bg-blue-50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">USA Customs — What You Need to Know</h2>
          <p className="text-gray-600 mb-4">All shipments entering the United States are subject to US Customs and Border Protection (CBP) clearance. XFas provides end-to-end documentation assistance including:</p>
          <ul className="space-y-2">
            {[
              'Commercial invoice preparation',
              'Correct HS/HTS code classification',
              'Guidance on duties and de minimis thresholds (currently USD 800 for informal entry)',
              'Prohibited and restricted items advisory',
            ].map((item) => (
              <li key={item} className="flex gap-3">
                <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {[
              {
                q: 'How long does it take to send a courier from India to the USA?',
                a: 'Express services typically deliver in 2–5 business days. Economy services take 7–12 business days depending on the destination state and carrier routing.',
              },
              {
                q: 'What is the cheapest way to send a parcel from India to the USA?',
                a: 'Economy air freight is the most cost-effective option for non-urgent shipments. Contact XFas for a tailored quote based on weight, dimensions, and destination zip code.',
              },
              {
                q: 'Can I send commercial goods from India to the USA?',
                a: 'Yes. XFas handles commercial shipments including export documentation, e-way bills, and Indian customs export compliance.',
              },
            ].map(({ q, a }) => (
              <div key={q} className="border-b border-gray-200 pb-6">
                <p className="font-semibold text-gray-800 mb-2">{q}</p>
                <p className="text-gray-600">{a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Related links */}
        <section className="bg-gray-50 rounded-2xl p-6">
          <p className="text-gray-600 text-sm">Also shipping to: <a href="/send-courier-from-india-to-uk/" className="text-orange-500 hover:underline font-medium">United Kingdom</a> · <a href="/send-courier-from-india-to-europe/" className="text-orange-500 hover:underline font-medium">Europe</a></p>
        </section>

        {/* CTA */}
        <section className="text-center bg-orange-500 rounded-2xl py-12 px-6 text-white">
          <h2 className="text-2xl font-bold mb-3">Ready to Ship to the USA?</h2>
          <p className="mb-6 text-orange-100">Get an instant quote and compare carrier rates in seconds.</p>
          <a href="/#get-quote" className="inline-block bg-white text-orange-500 font-semibold px-8 py-3 rounded-lg hover:bg-orange-50 transition-colors">
            Get a Quote
          </a>
        </section>

      </div>
    </div>
  );
};

export default CourierToUSAPage;
